import { getMonthName } from "@repo/shared";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import { useContainerWidth } from "@/lib/hooks/use-container-width";
import type { ValueHistoryDatum } from "@/lib/utils/vega-chart-shared";
import { createValueHistoryChartSpec } from "./vega-specs";

type ValueHistoryChartProps = {
  /** Points oldest first, tooltip text preformatted by the caller. */
  data: ValueHistoryDatum[];
  height?: number;
  primaryColor?: string;
  lang?: string;
};

/**
 * A single value-over-time line (e.g. a client's total value week by week)
 * with a hover guide and tooltip. Fills its container's width.
 */
export function ValueHistoryChart({
  data,
  height = 260,
  primaryColor,
  lang = "en_GB",
}: ValueHistoryChartProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";

  const spec = useMemo(() => {
    if (data.length === 0 || containerWidth === null) return null;
    const monthNames = Array.from({ length: 12 }, (_, i) => getMonthName(i, lang, "short"));
    return createValueHistoryChartSpec(data, {
      width: containerWidth,
      height,
      isDark,
      primaryColor,
      lang,
      monthNames,
    });
  }, [data, containerWidth, height, isDark, primaryColor, lang]);

  return (
    <div
      ref={containerRef}
      className="w-full [&_svg]:max-w-full [&_svg]:h-auto"
      style={{ minHeight: height }}
    >
      {spec && (
        <VegaEmbed
          spec={spec}
          options={{
            actions: false,
            renderer: "svg",
            tooltip: { theme: isDark ? "dark" : "light" },
          }}
        />
      )}
    </div>
  );
}
